import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Package, RefreshCw, Timer } from 'lucide-react';
import type { TicketCustomer, TicketPayment, TicketRecord } from '@/lib/api/tickets.js';
import { sendNpsSurveyNow, sendReviewRequestNow } from '@/lib/api/tickets.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';
import type { StoreSettings } from '@/lib/api/stores.js';
import type { TicketPartsGroup } from '@/lib/api/ticket-parts.js';
import { formatCurrency } from '@/lib/format.js';
import { depositRemaining } from '@/lib/deposit.js';
import { PrintButton } from '@/components/printing/PrintButton.js';
import { usePrintNodeConfigured } from '@/hooks/usePrintNodeConfigured.js';
import { TicketInsuranceSection } from '@/components/tickets/TicketInsuranceSection.js';
import { TicketPaymentsPanel } from '@/components/payments/TicketPaymentsPanel.js';
import { TicketWaiversSection } from '@/components/ticket-detail/TicketWaiversSection.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function TicketDetailSidebar({
  ticket,
  customer,
  customerName,
  estimatedProfit,
  partsCost,
  store,
  partsGroups = [],
  payments = [],
  onCheckout,
  onCollectDeposit,
  onPaymentsChanged,
  onViewPortal,
  onCopyPortalLink,
  onSendPortalLink,
  onLogTime,
  onOrderPart,
  onCreateWarranty,
  onSendSms,
  onSendEmail,
  onCollectPreWaiver,
  onCollectPostWaiver,
  estimatesSection,
}: {
  ticket: TicketRecord;
  customer?: TicketCustomer | null;
  customerName: string;
  estimatedProfit?: string | null;
  partsCost?: string | null;
  store?: StoreSettings | null;
  partsGroups?: TicketPartsGroup[];
  payments?: TicketPayment[];
  onCheckout: () => void;
  onCollectDeposit: () => void;
  onPaymentsChanged?: () => void;
  onViewPortal: () => void;
  onCopyPortalLink?: () => void;
  onSendPortalLink?: () => void;
  onLogTime: () => void;
  onOrderPart: () => void;
  onCreateWarranty: () => void;
  onSendSms: () => void;
  onSendEmail: () => void;
  onCollectPreWaiver?: () => void;
  onCollectPostWaiver?: () => void;
  warrantyActive: boolean;
  estimatesSection?: ReactNode;
}) {
  const balanceDue = Number(ticket.balanceDue ?? 0);
  const depositDue = depositRemaining(ticket.depositAmount, ticket.depositCollected);
  const points = Number(customer?.pointsBalance ?? customer?.loyaltyPointsBalance ?? 0);
  const profitNum = estimatedProfit != null ? Number(estimatedProfit) : null;
  const { hasPrintNode: hasLabelPrinter } = usePrintNodeConfigured('label');
  const { hasPrintNode: hasReceiptPrinter } = usePrintNodeConfigured('receipt');
  const showReceipt = ticket.status === 'closed' || Number(ticket.paidTotal ?? 0) > 0;
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const showDelayedComms = isTenantAdmin && ticket.status === 'closed';

  const npsMut = useMutation({
    mutationFn: () => sendNpsSurveyNow(ticket.id),
    onSuccess: (data) =>
      toastSuccess(
        data.dryRun
          ? 'NPS survey dry-run logged (COMMS_DRY_RUN — no real SMS)'
          : 'NPS survey SMS sent to customer',
      ),
    onError: (err) => toastError(parseApiError(err).message),
  });
  const reviewMut = useMutation({
    mutationFn: () => sendReviewRequestNow(ticket.id),
    onSuccess: (data) =>
      toastSuccess(
        data.dryRun
          ? 'Review request dry-run logged (COMMS_DRY_RUN — no real SMS)'
          : 'Review request SMS sent to customer',
      ),
    onError: (err) => toastError(parseApiError(err).message),
  });

  const taxMode = store?.taxMode ?? 'parts_only';
  const taxRate = Number(store?.taxRate ?? 0);
  const showPartsOnReceipt = store?.showPartsOnReceipt ?? false;
  const receiptParts = showPartsOnReceipt
    ? partsGroups.flatMap((g) => g.parts.filter((p) => p.showOnReceipt))
    : [];

  return (
    <aside className="space-y-4 lg:sticky lg:top-20 lg:w-80 lg:shrink-0">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">👤 Customer</h2>
          {customer?.id ? (
            <Link to={`/customers/${customer.id}`} className="text-xs font-semibold text-primary">Edit</Link>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-lg font-bold text-slate-950">{customerName}</p>
          <CustomerStatusBadge
            status={customer?.customerStatus}
            vipLabel={store?.vipLabel ?? null}
            flaggedLabel={store?.flaggedLabel ?? null}
            reason={customer?.statusReason ?? null}
          />
        </div>
        {(customer?.phone ?? ticket.customerPhoneOverride) ? (
          <div className="mt-2 flex items-center justify-between text-sm">
            <span>📱 {customer?.phone ?? ticket.customerPhoneOverride}</span>
            <button type="button" className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold" onClick={onSendSms}>SMS</button>
          </div>
        ) : null}
        {(customer?.email ?? ticket.customerEmailOverride) ? (
          <div className="mt-1 flex items-center justify-between text-sm">
            <span className="truncate">📧 {customer?.email ?? ticket.customerEmailOverride}</span>
            <button type="button" className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold" onClick={onSendEmail}>Email</button>
          </div>
        ) : null}
        <div className="mt-3 space-y-1 text-sm text-slate-600">
          <p>🎁 Loyalty: {points} pts ({formatCurrency(points / 100)})</p>
          <p>💳 Store Credit: {formatCurrency(customer?.storeCreditBalance ?? 0)}</p>
          <p>📊 Visits: {customer?.totalTickets ?? 0} · Spent: {formatCurrency(customer?.totalSpend ?? 0)}</p>
        </div>
        {customer?.id ? (
          <Link to={`/customers/${customer.id}`} className="mt-3 block text-sm font-semibold text-primary">
            View Customer Profile →
          </Link>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">💰 Billing</h2>
        <dl className="space-y-1.5 text-sm">
          {receiptParts.map((part) => (
            <div key={part.id} className="flex justify-between text-slate-700">
              <dt className="truncate pr-2">{part.partName}</dt>
              <dd>{formatCurrency(part.totalRetail)}</dd>
            </div>
          ))}
          <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd>{formatCurrency(ticket.subtotal)}</dd></div>
          {taxMode === 'parts_only' && Number(ticket.partsRetailTotal ?? 0) > 0 && !showPartsOnReceipt ? (
            <div className="flex justify-between text-slate-500">
              <dt>Parts (internal)</dt>
              <dd>—</dd>
            </div>
          ) : null}
          <div className="flex justify-between"><dt className="text-slate-500">Discount</dt><dd>-{formatCurrency(ticket.discountTotal ?? 0)}</dd></div>
          <div className="flex justify-between">
            <dt className="text-slate-500">
              {taxMode === 'parts_only' && !showPartsOnReceipt
                ? `Tax (parts @ ${(taxRate * 100).toFixed(1)}%)`
                : 'Tax'}
            </dt>
            <dd>{formatCurrency(ticket.taxTotal)}</dd>
          </div>
          {taxMode === 'parts_only' && !showPartsOnReceipt && Number(ticket.taxTotal) > 0 ? (
            <p className="text-xs text-slate-400">Tax included on parts</p>
          ) : null}
          <div className="flex justify-between border-t border-slate-200 pt-2 font-bold"><dt>Total</dt><dd>{formatCurrency(ticket.total)}</dd></div>
          {Number(ticket.depositCollected ?? 0) > 0 ? (
            <div className="flex justify-between text-slate-600"><dt>Deposit Paid</dt><dd>-{formatCurrency(ticket.depositCollected)}</dd></div>
          ) : ticket.depositSkipped ? (
            <div className="flex justify-between text-slate-500">
              <dt>Deposit</dt>
              <dd>Skipped (manager override)</dd>
            </div>
          ) : null}
          <div className="flex justify-between"><dt className="text-slate-500">Paid</dt><dd>{formatCurrency(ticket.paidTotal)}</dd></div>
          <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-black">
            <dt className={balanceDue > 0 ? 'text-red-600' : 'text-green-700'}>Balance Due</dt>
            <dd className={balanceDue > 0 ? 'text-red-600' : 'text-green-700'}>{formatCurrency(balanceDue)}</dd>
          </div>
        </dl>
        <div className="mt-4 border-t border-slate-200 pt-3">
          <TicketPaymentsPanel
            ticketId={ticket.id}
            ticketNumber={ticket.ticketNumber}
            customerName={customerName}
            paidTotal={ticket.paidTotal}
            balanceDue={ticket.balanceDue}
            ticketTotal={ticket.total}
            initialPayments={payments}
            compact
            {...(balanceDue > 0
              ? { onAddPayment: onCheckout }
              : ticket.depositRequired && depositDue > 0 && !ticket.depositSkipped && onCollectDeposit
                ? { onAddPayment: onCollectDeposit }
                : {})}
            {...(onPaymentsChanged ? { onChanged: onPaymentsChanged } : {})}
          />
        </div>
        {profitNum != null ? (
          <p
            className={`mt-2 text-sm font-semibold ${profitNum >= 0 ? 'text-green-700' : 'text-red-600'}`}
            title={partsCost != null ? `Parts cost: ${formatCurrency(partsCost)}` : undefined}
          >
            Est. Profit: {formatCurrency(estimatedProfit)}
            {partsCost != null ? ` · Parts cost: ${formatCurrency(partsCost)}` : ''}
          </p>
        ) : null}
        <div className="mt-4 space-y-2">
          {balanceDue > 0 ? (
            <button type="button" className="w-full rounded-xl bg-accent px-4 py-2.5 text-sm font-bold text-white" onClick={onCheckout}>
              💳 Checkout →
            </button>
          ) : null}
          {ticket.depositRequired && depositDue > 0 && !ticket.depositSkipped ? (
            <button type="button" className="w-full rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-bold text-amber-900" onClick={onCollectDeposit}>
              💰 Collect Deposit ({formatCurrency(depositDue)})
            </button>
          ) : null}
        </div>
      </section>

      {estimatesSection}

      <TicketWaiversSection
        ticket={ticket}
        {...(onCollectPreWaiver ? { onCollectPre: onCollectPreWaiver } : {})}
        {...(onCollectPostWaiver ? { onCollectPost: onCollectPostWaiver } : {})}
      />

      <TicketInsuranceSection ticketId={ticket.id} />

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">⚡ Quick Actions</h2>
        <div className="space-y-2">
          <PrintButton type="label" ticketId={ticket.id} hasPrintNode={hasLabelPrinter} className="w-full justify-center" />
          {showReceipt ? (
            <PrintButton type="receipt" ticketId={ticket.id} hasPrintNode={hasReceiptPrinter} className="w-full justify-center" />
          ) : null}
          <button type="button" className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50" onClick={onViewPortal}>
            <ExternalLink className="h-4 w-4" /> View Portal ↗
          </button>
          {onCopyPortalLink ? (
            <button type="button" className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50" onClick={onCopyPortalLink}>
              📋 Copy Portal Link
            </button>
          ) : null}
          {onSendPortalLink ? (
            <button type="button" className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50" onClick={onSendPortalLink}>
              📱 Send Portal Link
            </button>
          ) : null}
          <button type="button" className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50" onClick={onLogTime}>
            <Timer className="h-4 w-4" /> Log Time
          </button>
          <button type="button" className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50" onClick={onOrderPart}>
            <Package className="h-4 w-4" /> Order Part
          </button>
          <button type="button" className="flex w-full items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-900 hover:bg-violet-100" onClick={onCreateWarranty}>
            <RefreshCw className="h-4 w-4" /> Create Warranty Ticket
          </button>
          {showDelayedComms ? (
            <>
              <div className="my-2 border-t border-slate-100" />
              <p className="px-1 text-xs font-medium text-slate-500">
                Test delayed SMS (bypasses delay · respects dry-run)
              </p>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                disabled={npsMut.isPending || reviewMut.isPending}
                onClick={() => npsMut.mutate()}
              >
                {npsMut.isPending ? 'Sending NPS…' : 'Send NPS survey now'}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                disabled={npsMut.isPending || reviewMut.isPending}
                onClick={() => reviewMut.mutate()}
              >
                {reviewMut.isPending ? 'Sending review…' : 'Send review request now'}
              </button>
            </>
          ) : null}
        </div>
      </section>
    </aside>
  );
}
